import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styles from './accordion-content.module.scss';
import classnames from 'classnames';
import scrollTo from '../../modules/scroll-to';
import ExpandButton from '../expand-button';
import ChapterLink from './chapter-link';
import Markdown from '../markdown';

const FULLSCREEN_HEIGHT_RATIO = 0.95;
const FULLSCREEN_MARGIN = (1 - FULLSCREEN_HEIGHT_RATIO) / 2;

class AccordionContent extends Component {
  constructor() {
    super();
    this.initialState = {
      fullscreenImage: {
        index: null, // If index is null the popup is not shown.
        isSidebarImage: true,
      },
    };
    this.state = this.initialState;
    this.rowsEl = [];
    this.handleExpandClick = this.handleExpandClick.bind(this);
  }

  handleExpandClick({ index, isSidebarImage }) {
    // Sets the image as fullscreen image and scrolls so that the popup is in the middle
    this.setState({ fullscreenImage: { index, isSidebarImage } }, () => {
      const box = this.expandedImageContainer.getBoundingClientRect();
      const bodyOffset =
        document.body.getBoundingClientRect().y ||
        document.body.getBoundingClientRect().top;
      // For the popup/expandedImageContainer we use 80% of the viewport and center it.
      // This means we have 10% of the viewport height of margin.
      // Remove the bodyOffset (client rect return the Y pos of the viewport.)
      scrollTo(0, box.top - bodyOffset - window.parentViewportHeight * FULLSCREEN_MARGIN);
    });
  }

  renderSidebarImage({ content, index }) {
    if (!content.sidebarImage) return;
    const containerClassName = classnames({
      [styles.sideImageContainer]: true,
      [styles.sideImageContainerFirst]: index === 0,
    });

    const expandButton = content.hideSidebarExpand ? null : (
      <div className="desktopOnly">
        <ExpandButton
          onClick={() =>
            this.handleExpandClick({ index, isSidebarImage: true })
          }
        />
      </div>
    );
    return (
      <div className={containerClassName}>
        {content.sidebarImage.title ? (
          <p className={styles.imgTitle}>{content.sidebarImage.title}</p>
        ) : null}
        <img
          className={styles.image}
          onClick={() =>
            this.handleExpandClick({ index, isSidebarImage: true })
          }
          alt={content.sidebarImage.altText || 'Image'}
          src={'/contentful/' + content.sidebarImage.filename}
        />
        {expandButton}
      </div>
    );
  }
  renderInlineImage({ content, index }) {
    if (!content.inlineImage) return;

    return (
      <div className={styles.inlineImageContainer}>
        <p className={styles.imgTitle}>{content.inlineImage.title}</p>
        <img
          onClick={() =>
            this.handleExpandClick({ index, isSidebarImage: false })
          }
          className={styles.image}
          alt={content.inlineImage.altText || 'Image'}
          src={'/contentful/' + content.inlineImage.filename}
        />
        <div className="desktopOnly">
          <ExpandButton
            onClick={() =>
              this.handleExpandClick({ index, isSidebarImage: false })
            }
          />
        </div>
      </div>
    );
  }

  renderRows() {
    return this.props.contents.map((content, index) => {
      const headline = content.headline ? (
        <h4 className={styles.contentHeadline}>{content.headline}</h4>
      ) : null;
      return (
        <div
          key={content.id}
          ref={el => (this.rowsEl[index] = el)}
          className={styles.contentRow}
        >
          <div className="mobileOnly">
            <ChapterLink
              shouldRender={index === 0 && this.props.displayChapterLink}
              chapterLink={this.props.chapterLink}
            />
          </div>
          {headline}
          {this.renderInlineImage({ content, index })}

          <div className={styles.contentLinkImgRow}>
            <Markdown content={content.content} />
            <div className={styles.linkImgContainer}>
              <div className="desktopOnly">
                <ChapterLink
                  shouldRender={index === 0 && this.props.displayChapterLink}
                  chapterLink={this.props.chapterLink}
                />
              </div>
              {this.renderSidebarImage({ content, index })}
            </div>
          </div>
        </div>
      );
    });
  }

  renderExpandedImage() {
    const { fullscreenImage } = this.state;

    if (fullscreenImage.index === null) return;
    const imageIndex = fullscreenImage.index;
    const content = this.props.contents[imageIndex];
    const image = fullscreenImage.isSidebarImage
      ? content.sidebarImage
      : content.inlineImage;

    return (
      <div
        role="dialog"
        ref={el => {
          this.expandedImageContainer = el;
        }}
        style={{
          top: this.rowsEl[imageIndex].offsetTop,
          height: window.parentViewportHeight * FULLSCREEN_HEIGHT_RATIO,
        }}
        className={styles.expandedImageContainer}
      >
        <h4 className={styles.imgTitle}>{image.title}</h4>
        <button
          aria-label="Close Image Dialog"
          onClick={() => this.setState(this.initialState)}
          tabIndex={0}
          autoFocus
          className={styles.closeButton}
        >
          <img alt={'Close button cross'} src={'/img/cross.svg'} />
        </button>
        <div
          ref={el => (this.fullscreenImageContainer = el)}
          className={styles.fullscreenImageContainer}
        >
          <img
            onLoad={e => {
              const el = e.currentTarget;
              if (
                el.clientHeight + el.offsetTop >
                window.parentViewportHeight * FULLSCREEN_HEIGHT_RATIO
              ) {
                this.fullscreenImageContainer.style.height = '100%';
                el.style.height = '100%';
              }
            }}
            className={styles.image}
            alt={image.altText || 'Image'}
            src={'/contentful/' + image.filename}
          />
        </div>
      </div>
    );
  }

  render() {
    const { isOpened } = this.props;
    return (
      <div className={styles.contentContainer} aria-hidden={!isOpened}>
        {this.renderExpandedImage()}
        {this.renderRows()}
      </div>
    );
  }
}
AccordionContent.propTypes = {
  chapterLink: PropTypes.string,
  displayChapterLink: PropTypes.bool,
  contents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      headline: PropTypes.string,
      inlineImage: PropTypes.shape({
        filename: PropTypes.string.isRequired,
        altText: PropTypes.string.isRequired,
      }),
      sidebarImage: PropTypes.shape({
        filename: PropTypes.string.isRequired,
        altText: PropTypes.string.isRequired,
      }),
    })
  ),
};
export default AccordionContent;



// WEBPACK FOOTER //
// ./src/components/accordion/accordion-content.js